/* ── KanbanGroupCard ───────────────────────────────────────────────────
   Individual draggable group or account card inside each column and container,
   supporting both directory groups and individual accounts.
   ──────────────────────────────────────────────────────────────────────── */

import { GripVertical, Users, User } from "lucide-react";
import { Checkbox } from "../../../components/ui/checkbox";
import { useSeverityColorSync } from "../../../../stores/severityColorStore";
import {
  getUnifiedColumnMeta,
  type UnifiedColumn,
  type UnifiedGroup,
} from "../constants/pagAccountConfigConstants";
import { cn } from "../../../components/ui/utils";

export interface KanbanGroupCardProps {
  group: UnifiedGroup;
  column: UnifiedColumn;
  editing: boolean;
  isSelected: boolean;
  isModified: boolean;
  onToggleSelect: (id: string) => void;
  selectedIds: string[];
  variant?: "default" | "compact";
}

export const KanbanGroupCard = ({
  group,
  column,
  editing,
  isSelected,
  isModified,
  onToggleSelect,
  selectedIds,
  variant = "default",
}: KanbanGroupCardProps) => {
  const severityColors = useSeverityColorSync();
  const meta = getUnifiedColumnMeta(column, severityColors);
  const color = meta.color;
  const isAccount = group.itemType === "ACCOUNT";

  return (
    <div
      draggable={editing}
      onDragStart={(e) => {
        const ids = selectedIds.includes(group.id) ? selectedIds : [group.id];
        e.dataTransfer.setData("text/plain", ids.join(","));
      }}
      className={cn(
        "group relative flex items-center gap-2.5 rounded-xl border p-2.5 transition-all text-xs",
        isSelected
          ? "border-blue-500/80 bg-blue-500/15 ring-1 ring-blue-500/40 shadow-md"
          : isModified
            ? "border-indigo-500/60 bg-indigo-500/10 ring-1 ring-indigo-500/30"
            : "border-border/60 bg-card/90 hover:border-border hover:bg-card hover:shadow-sm",
        editing && "cursor-grab active:cursor-grabbing",
      )}
      style={
        isSelected || isModified
          ? undefined
          : { borderLeft: `3px solid ${color}` }
      }
    >
      {editing ? (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(group.id)}
          aria-label={`Select ${group.groupName}`}
          className="size-3.5 shrink-0 rounded border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
        />
      ) : (
        <div
          className="flex size-7 shrink-0 items-center justify-center rounded-full"
          style={{ background: `${color}18`, color }}
        >
          {isAccount ? <User className="size-3.5" /> : <Users className="size-3.5" />}
        </div>
      )}

      {editing && (
        <div
          className="flex size-6 shrink-0 items-center justify-center rounded-full"
          style={{ background: `${color}18`, color }}
        >
          {isAccount ? <User className="size-3" /> : <Users className="size-3" />}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p
            className="truncate text-xs font-semibold leading-4 text-foreground"
            title={group.groupName}
          >
            {group.groupName}
          </p>
          {isAccount && (
            <span className="shrink-0 rounded bg-indigo-500/15 px-1 py-0.2 text-[9px] font-semibold text-indigo-400">
              Account
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-[10px] text-muted-foreground">
          {isModified && (
            <span className="rounded bg-indigo-500/20 px-1 py-0.2 font-semibold text-indigo-300">
              Unsaved
            </span>
          )}

          {isAccount ? (
            <span>
              {group.userName ? `@${group.userName}` : group.email || "Direct Account"}
            </span>
          ) : (
            <span>
              {group.accountCount} {group.accountCount === 1 ? "account" : "accounts"}
            </span>
          )}

          {group.sourceSystemName && (
            <span className="truncate max-w-[90px]">
              • {group.sourceSystemName}
            </span>
          )}
        </div>
      </div>

      {editing && (
        <GripVertical className="size-3.5 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
      )}
    </div>
  );
};
